/**
 * Referrer portal availability, as reported by the backend.
 *
 * The backend (backend/config/referrerPortal.js) is the source of truth for both
 * the flag and the notice wording, so the message can be changed with a backend
 * deploy alone — frontend and backend are separate Render services.
 *
 * Fails CLOSED: if the status cannot be fetched we treat submissions as
 * disabled and show the fallback notice. Showing the notice when submissions
 * are actually available is a minor annoyance; offering a referral form that
 * the API will reject wastes a referrer's time filling it in.
 */

import { useEffect, useState } from 'react';
import API_BASE_URL from '../config/api';

/** Used only when the status endpoint is unreachable. Backend text wins. */
export const FALLBACK_NOTICE =
  'At this time self generated referrals have been disabled. Thank you for all your help '
  + 'developing this. We will continue to work on our system and all the help and feedback '
  + 'you have given has been invaluable. Please continue to send referrals through our normal '
  + 'pathway at reception@angsm.co.nz and we will endeavour to get to them as quickly as possible.';

/**
 * @returns {{loading: boolean, submissionsEnabled: boolean, notice: string|null}}
 */
export function useReferrerPortalStatus() {
  const [status, setStatus] = useState({
    loading: true,
    submissionsEnabled: false,
    notice: FALLBACK_NOTICE,
  });

  useEffect(() => {
    let cancelled = false;

    fetch(`${API_BASE_URL}/referrer-auth/portal-status`)
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error(`HTTP ${res.status}`))))
      .then((data) => {
        if (cancelled) return;
        setStatus({
          loading: false,
          submissionsEnabled: Boolean(data.submissionsEnabled),
          notice: data.notice || (data.submissionsEnabled ? null : FALLBACK_NOTICE),
        });
      })
      .catch(() => {
        if (cancelled) return;
        setStatus({ loading: false, submissionsEnabled: false, notice: FALLBACK_NOTICE });
      });

    return () => { cancelled = true; };
  }, []);

  return status;
}
